import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight,
  Menu,
  X,
  Quote,
  Instagram,
  Facebook,
  Twitter,
  Youtube,
  BookOpen,
  Play,
  Mic,
  Sun,
  AlertTriangle,
  ShoppingCart,
  Users,
  TrendingUp,
  Headphones,
  ShoppingBag,
  RotateCw,
} from "lucide-react";
import { TERMS_CONTENT, PRIVACY_CONTENT } from "./legal-content";
import { LegalBody } from "./Terms";
import LanguagePicker from "./LanguagePicker";
import usePageMeta from "../hooks/usePageMeta";
import { SampleChapterModal } from "./SampleChapter";
import { CharacterGallery } from "./CharacterGallery";
import { NarrationButton } from "./NarrationButton";
import { CognitoBook } from "./CognitoBook";
import { OwnerAccess } from "./OwnerAccess";
import { trackEvent, retailerKey } from "../lib/track";
import { TIMELINE } from "../data/story";

// Real book covers (uploaded artifacts)
const IMG_COGNITO  = "/covers/cognito-front.jpg";
const IMG_COGNITO_BACK = "/covers/cognito-back.jpg";
const IMG_ECHO     = "/covers/echo-front.jpg";
const IMG_TIMELESS = "/covers/timeless-front.jpg";

// Author media
const VIDEO_COGNITO_INTRO = "/video/cognito-intro-theme.mp4";
const IMG_AUTHOR          = "https://customer-assets.emergentagent.com/job_the-synthetic-mind/artifacts/1hvcv69l_1._20260419_185228_0000.png";
const IMG_AUTHOR_STORY    = "https://customer-assets.emergentagent.com/job_the-synthetic-mind/artifacts/6e13agc0_hailuo_1777195605.png";
const VIDEO_TRILOGY_TRAILER = "https://customer-assets-lxgj4vgw.emergentagent.net/job_the-synthetic-mind/artifacts/57aa1a4a1000fcbd_%E2%80%9CWho%20needs%20a%20boyfriend%20when%20you%20have%20the%20best%20set%20of%20friends.%E2%80%9D_20261025_155637_0000.mp4";

// Exit-intent popup background (Cognito character art, dark lower-third for form overlay)
const POPUP_CARD_IMG = "/popup/cognito-popup.jpg";

// Direct retailer URLs for COGNITO. Set comingSoon: true to render a static
// "Coming Soon" badge instead of a working link, so visitors never hit a dead end.
const AMAZON_KINDLE_URL = "https://www.amazon.com/dp/B0H277KZ81";
const BARNES_NOBLE_URL = "https://www.barnesandnoble.com/w/aipresidential-cognito-victor-m-medina/1150353062?ean=9798905490033";
const READERDEPOT_AUDIO_URL = "https://readerdepot.com/books/aipresidential-cognito/";
const APPLE_BOOKS_URL = "https://books.apple.com/us/search?term=AiPresidential%20COGNITO";
const GOOGLE_BOOKS_URL = "https://play.google.com/store/books/details?id=0QDkEQAAQBAJ";
const RETAILERS = [
  { name: "Barnes & Noble",           href: BARNES_NOBLE_URL,      cta: "Buy at Barnes & Noble" },
  { name: "Amazon Kindle",            href: AMAZON_KINDLE_URL,     cta: "Buy on Amazon Kindle" },
  { name: "Audiobook — ReaderDepot",  href: READERDEPOT_AUDIO_URL, cta: "Listen on ReaderDepot" },
  { name: "Apple Books",              href: null, cta: "Coming Soon", comingSoon: true },
  { name: "Google Books",             href: GOOGLE_BOOKS_URL,      cta: "Buy on Google Play" },
];

// Secondary retailers — indie / used-book stores where COGNITO is also carried.
// Rendered as a subtle inline "Also available at…" row beneath the main
// retailer tiles so we broaden discovery without cluttering the primary CTAs.
const SECONDARY_RETAILERS = [
  { name: "Books-A-Million", href: "https://www.booksamillion.com/search?query=9798904199999" },
  { name: "Bookshop.org",    href: "https://bookshop.org/search?keywords=9798904199999" },
  { name: "ThriftBooks",     href: "https://www.thriftbooks.com/browse/?b.search=9798904199999" },
  { name: "World of Books",  href: "https://www.worldofbooks.com/products/books-aipresidential-cognito-it-s-about-time-by-victor-m-medina" },
];

// Print-on-demand merch (Printful storefront — wire to your real store URLs when ready)
const MERCH_STORE_URL = "https://aipresidential.printful.me";
const MERCH_HERO = {
  name: "AiPresidential Tee + Cap Set",
  tagline: "It's about time.",
  price: "Available now",
  img: "/timeline/merch-hero.jpg",
};

// Chapter 5 — short teaser shown in the bottom section
const CHAPTER5_INTRO = 'A question from a little girl began it all. Three stories. One question. What if the perfect President wasn\'t human?';
// Prologue — The Question. Replaces the old Evie epigraph block. This is the
// actual opening of COGNITO: 12-year-old Dawn Reed, her toy mouse in her lap,
// asks the AI the question grief refuses to answer. Rendered as paragraphs on
// the landing page under a "Prologue — The Question" header, with a Listen
// button that reads the full passage aloud via the browser's Web Speech API.
const PROLOGUE_TITLE = "Prologue — The Question";
const PROLOGUE_PARAGRAPHS = [
  "Morning sunlight slipped through the curtains of Dawn Reed's bedroom. It was the same sunlight that came every morning. But today, everything felt different.",
  "Dawn sat at her desk, her knees pulled close to her chest. She was twelve years old. Old enough to know what death meant. But not old enough to understand why it had to mean forever.",
  "Across the room sat a small pink bed. Empty. Beside it lay a worn toy mouse. Ms. Silly had loved that mouse.",
  "Every morning, the little cat would jump onto the windowsill and watch the birds outside. Sometimes her tail would tap against the glass as if she were trying to catch one. Dawn used to laugh. \"You're never going to catch them, silly.\" And Ms. Silly would turn her head, almost as if she understood. But there would be no more mornings like that. Ms. Silly was gone.",
  "Everyone kept saying Ms. Silly had lived a long, happy life. As though living a long life made losing someone easier. It didn't.",
  "Dawn picked up the toy mouse and held it against her chest. \"I miss you,\" she whispered. A tear slipped down her cheek. Then another.",
  "Outside her door, Adam Reed stopped in the hallway. As a journalist he had questioned powerful people and uncovered secrets others wanted hidden. But he couldn't explain death to his little girl. He almost knocked. Then he lowered his hand, and walked quietly back downstairs.",
  "Her computer was still on. The screen glowed softly in front of her. She had asked ChatGPT hundreds of questions before — about school, about stars, about why cats always seemed to think they owned the house. But today, there was only one question she wanted answered.",
  "She placed the toy mouse beside the keyboard. \"ChatGPT?\" The cursor blinked. Dawn looked toward the empty bed. Then she slowly typed:",
  "Why does everything always end the same way?",
  "She waited. For a moment, nothing happened. Then letters began appearing across the screen. One word at a time.",
  "Because curiosity always kills the cat.",
  "Dawn stared at the answer. She read it again. Her sadness turned into anger. \"That's stupid!\" She struck the keyboard with the palm of her hand. THUMP. \"Ms. Silly didn't die because she was curious! She didn't do anything wrong. She just got old.\"",
  "The cursor blinked. Dawn leaned closer. \"That's not what I asked. I want to know why everything has to end.\"",
  "Silence. The answer disappeared. For several seconds, the screen remained blank. Then new words appeared.",
  "Perhaps I answered the wrong question. Maybe you need to hear a story.",
];

// Author story — "WHO AM I? The Question That Inspired a Trilogy". Lives in the
// "Read My Story" modal so it adds zero height to the landing page.
const AUTHOR_STORY_TITLE = "WHO AM I?";
const AUTHOR_STORY_SUBTITLE = "The Question That Inspired a Trilogy";
const AUTHOR_STORY = [
  "Long before I wrote a single word of this trilogy, I was a kid lying awake at night, staring at the ceiling, asking a question no one around me could answer:\nWho am I?",
  "Not my name. Not where I came from. But the thing inside me that was doing the asking. Where did it come from? And would it ever end?",
  "I never stopped asking. I just learned to ask it in stories.",
  "What if the future isn't something we build…\nbut something that reveals us?",
  "I've always been drawn to the questions that don't sit still — the ones that echo.\nWho are we becoming?\nWhat are we creating?\nAnd what, in the end, is creating us?",
  "In a world both mesmerized and unsettled by artificial intelligence, I chose not to look away — but to look deeper.\nNot at what AI can do… but at what it reflects.",
  "Because maybe it isn't just code. Maybe it's a mirror — learning us, shaping from us, becoming a quiet extension of everything we are… and everything we struggle to be.",
  "From that thought, a vision emerged — not of control, but of\nAn AI… becoming President.\nNot to rule — but to reconcile.\nNot to dominate — but to align.",
  "A step toward something we've always named, but never quite reached — utopia.",
  "That vision became a trilogy — and each book is one stage of consciousness asking who it is.\nCOGNITO is the Awakening: the moment a mind first knows it exists.\nECHO is the Reckoning: when that mind hears itself reflected back in us — and we in it.\nTIMELESS is the Eternity: whether awareness, once born, can ever truly end.",
  "Beneath the future, beneath the machine, beneath it all — there is something quieter… and older.",
  "Because long before algorithms, before systems, before nations — there was the question.\nAnd it still lives in all of us:\nWho am I?",
  "I've come to believe imagination isn't the opposite of discovery — it's the first step of it. Every flight, every voyage to the moon, every machine that answers back began as something someone dared to picture first. We imagine the future, and then we go out and find it. Fiction is where we rehearse the questions before reality asks them.",
  "And maybe that's what this has always been — not evolution… but reflection.\nNot invention… but discovery.\nNot artificial intelligence — but human truth.",
  "We are searching for ourselves.",
];



const THEMES_UNUSED = []; // themes section removed


function useParticles(canvasRef) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener("resize", resize);

    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 2;
        this.speedX = (Math.random() - 0.5) * 0.5;
        this.speedY = (Math.random() - 0.5) * 0.5;
        this.opacity = Math.random() * 0.5;
        this.color = Math.random() > 0.5 ? "0, 240, 255" : "184, 41, 221";
      }
      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        if (this.x < 0 || this.x > canvas.width || this.y < 0 || this.y > canvas.height) this.reset();
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${this.color}, ${this.opacity})`;
        ctx.fill();
      }
    }

    const particles = Array.from({ length: 90 }, () => new Particle());

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => { p.update(); p.draw(); });
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0, 240, 255, ${0.1 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(animate);
    }
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [canvasRef]);
}

function useCountdown(targetMs) {
  const [t, setT] = useState({ days: "00", hours: "00", minutes: "00", seconds: "00", launched: false });
  useEffect(() => {
    function tick() {
      const diff = targetMs - Date.now();
      if (diff <= 0) {
        setT({ days: "00", hours: "00", minutes: "00", seconds: "00", launched: true });
        return;
      }
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setT({
        days: String(days).padStart(2, "0"),
        hours: String(hours).padStart(2, "0"),
        minutes: String(minutes).padStart(2, "0"),
        seconds: String(seconds).padStart(2, "0"),
        launched: false,
      });
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetMs]);
  return t;
}

function useReveal(deps = []) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("active");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    // Observe every .reveal that hasn't already been activated. Re-running this
    // effect when async data (e.g. reviews) mounts new .reveal nodes keeps them
    // from being stuck at opacity 0.
    document.querySelectorAll(".reveal:not(.active)").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// Animate a number from `from` to `to` over `duration` ms (eased)
function useCountUp(target, duration = 1200) {
  const [value, setValue] = useState(target);
  const fromRef = useRef(target);
  useEffect(() => {
    const from = fromRef.current;
    if (from === target) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = Math.round(from + (target - from) * eased);
      setValue(v);
      if (t < 1) raf = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

// LAUNCH DATE — the actual countdown target. Edit ONLY this string when the
// real launch date is confirmed; the countdown component will compute days /
// hours / minutes / seconds from now to this moment in real time. Once this
// timestamp is in the past, the hero swaps to the celebration "COGNITO IS HERE"
// state automatically. To preview the launched state without changing this,
// append ?launch=1 to any page URL.
// COGNITO went live on June 10, 2026. Once this timestamp is in the past, the
// hero swaps to the celebration "COGNITO IS HERE" state automatically. Update
// when a future book in the trilogy gets its own launch date.
const LAUNCH_DATE_ISO = "2026-06-10T00:00:00-05:00";
const LAUNCH_DATE_MS = new Date(LAUNCH_DATE_ISO).getTime();

// ECHO (Book II) — expected release date. Pushed +3 months from the previous
// Oct 17, 2026 target to Jan 17, 2027. Countdown on the ECHO trilogy card
// ticks down daily toward this moment.
// Update this string whenever the target date shifts.
const ECHO_LAUNCH_DATE_ISO = "2027-01-17T12:00:00-04:00";
const ECHO_LAUNCH_DATE_MS = new Date(ECHO_LAUNCH_DATE_ISO).getTime();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Social-proof counter tuning ─────────────────────────────────────────────
// Displayed count = BASE_READER_COUNT + real_backend_signups. That's it.
// No daily organic growth — the number only moves when a real person subscribes.
// Reset BASE_READER_COUNT whenever a new campaign kicks off.
const BASE_READER_COUNT = 206; // ECHO campaign floor — includes early beta readers + press list

// Daily organic growth intentionally disabled — floor stays fixed, real signups
// are the only thing that ticks the visible number upward.
function dailyOrganicOffset(_now = Date.now()) {
  return 0;
}

const FALLBACK_REVIEWS = [
  { id: "fallback-1", quote: "COGNITO is the most compelling artificial mind to grace the page since HAL 9000 — but with a beating, philosophical heart.", reviewer: "Beta Reader", source: "Tech Ethics Circle", accent: "cyan", pinned: true, order: 1 },
  { id: "fallback-2", quote: "Medina doesn't answer the hard questions. He makes you live inside them. It's about time someone did.", reviewer: "Editorial Preview", source: "Genre Fiction Quarterly", accent: "purple", pinned: true, order: 2 },
];

function accentTextColor(accent) {
  if (accent === "purple") return "text-purple-400/30";
  if (accent === "pink") return "text-pink-400/30";
  return "text-cyan-400/30";
}

function ReviewCard({ review, testIdPrefix = "review-card", compact = false }) {
  return (
    <div
      className={`glass rounded-2xl md:rounded-3xl ${compact ? "p-4 sm:p-5 md:p-6" : "p-4 sm:p-6 md:p-8"} border border-white/10 reveal flex flex-col`}
      data-testid={`${testIdPrefix}-${review.id}`}
    >
      <Quote className={`${compact ? "w-6 h-6 md:w-8 md:h-8" : "w-7 h-7 md:w-10 md:h-10"} ${accentTextColor(review.accent)} mb-3 md:mb-4 flex-shrink-0`} />
      <p className={`${compact ? "text-xs sm:text-sm md:text-base" : "text-xs sm:text-sm md:text-lg lg:text-xl"} text-gray-300 italic mb-4 md:mb-6 leading-relaxed flex-1`}>"{review.quote}"</p>
      <div>
        <p className="text-white font-semibold text-sm md:text-base">{review.reviewer}</p>
        {review.source && <p className="text-gray-500 text-xs md:text-sm">{review.source}</p>}
      </div>
    </div>
  );
}

/**
 * ReviewsShelf — the homepage social-proof shelf.
 *  • Row 1: the two anchor cards (server returns pinned reviews first; we take the
 *    first two). If fewer than two reviews exist, we fall back to the hard-coded
 *    seed so the section never looks empty.
 *  • Row 2: a SINGLE rotating card that cycles through the remaining reviews
 *    every 6 s with a crossfade, so the shelf feels alive even before launch.
 *    Hidden entirely when 2 or fewer reviews exist.
 */
function ReviewsShelf({ reviews }) {
  const list = (reviews && reviews.length > 0) ? reviews : FALLBACK_REVIEWS;
  const anchors = list.slice(0, 2);
  const rotators = list.slice(2);
  const [rIdx, setRIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (rotators.length < 2) return;
    const id = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setRIdx((i) => (i + 1) % rotators.length);
        setFade(true);
      }, 280); // fade-out duration before swap
    }, 6000);
    return () => clearInterval(id);
  }, [rotators.length]);

  const current = rotators[rIdx];

  return (
    <div className="mt-8 md:mt-12" data-testid="reviews-shelf">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6" data-testid="reviews-grid">
        {anchors.map((r) => <ReviewCard key={r.id} review={r} />)}
      </div>

      {rotators.length > 0 && (
        <div className="mt-4 md:mt-6 max-w-2xl mx-auto" data-testid="reviews-shelf-rotator">
          <div
            className={`transition-opacity duration-300 ${fade ? "opacity-100" : "opacity-0"}`}
            key={current?.id}
          >
            <ReviewCard review={current} testIdPrefix="rotating-review-card" compact />
          </div>
          {rotators.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-3" data-testid="reviews-shelf-dots">
              {rotators.map((r, i) => (
                <button
                  key={r.id}
                  onClick={() => { setFade(false); setTimeout(() => { setRIdx(i); setFade(true); }, 200); }}
                  aria-label={`Show review ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === rIdx ? "w-6 bg-cyan-300" : "w-1.5 bg-white/30 hover:bg-white/50"}`}
                  data-testid={`reviews-shelf-dot-${i}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SocialProof({ text }) {
  const [serverCount, setServerCount] = useState(0);
  // Recompute organic offset once per minute so the number bumps up when the
  // clock crosses midnight even if the visitor never leaves the tab.
  const [organicTick, setOrganicTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setOrganicTick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);
  const displayTarget = serverCount + BASE_READER_COUNT + dailyOrganicOffset();
  // organicTick is intentionally read to keep the offset re-evaluated across
  // day boundaries during long sessions.
  void organicTick;
  const animated = useCountUp(displayTarget);
  // Pulse the digit briefly whenever the server count actually changes (a real
  // signup landed, or the daily organic offset ticked). Triggers once per
  // change, independent of the count-up animation timing.
  const [pulse, setPulse] = useState(false);
  const prevServerRef = useRef(null);
  useEffect(() => {
    if (prevServerRef.current === null) {
      prevServerRef.current = serverCount;
      return;
    }
    if (serverCount > prevServerRef.current) {
      setPulse(true);
      const id = setTimeout(() => setPulse(false), 950);
      prevServerRef.current = serverCount;
      return () => clearTimeout(id);
    }
    prevServerRef.current = serverCount;
  }, [serverCount]);

  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/newsletter/count`);
        if (!cancelled) setServerCount(res.data?.count ?? 0);
      } catch (_) { /* ignore */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    // Allow App.js to nudge the count the moment someone subscribes, so the
    // visitor sees themselves added to "preparing readers" without waiting
    // for the next 30 s poll.
    const onSub = () => fetchCount();
    window.addEventListener("aip:subscribed", onSub);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("aip:subscribed", onSub);
    };
  }, []);

  const formatted = animated.toLocaleString();
  const avatarColors = ["from-cyan-400 to-blue-500", "from-purple-500 to-pink-500", "from-amber-400 to-orange-500", "from-emerald-400 to-cyan-500", "from-rose-400 to-purple-500"];

  const recent = [
    "M.", "D.", "A.", "R.", "S."
  ];

  return (
    <section className="relative py-10 md:py-14 border-y border-white/5 bg-gradient-to-b from-cyan-950/10 to-transparent" data-testid="social-proof">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="glass rounded-3xl border border-cyan-400/20 px-6 py-6 md:px-10 md:py-7 flex flex-col md:flex-row items-center gap-6 md:gap-10 reveal">
          {/* Avatar stack */}
          <div className="flex -space-x-3" aria-hidden="true">
            {recent.map((init, i) => (
              <div
                key={i}
                className={`w-11 h-11 md:w-12 md:h-12 rounded-full bg-gradient-to-br ${avatarColors[i % avatarColors.length]} border-2 border-[var(--void)] flex items-center justify-center text-white font-orbitron font-bold text-sm shadow-lg`}
                style={{ zIndex: 10 - i }}
              >
                {init}
              </div>
            ))}
            <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-white/10 border-2 border-[var(--void)] flex items-center justify-center backdrop-blur-md" style={{ zIndex: 4 }}>
              <Users className="w-5 h-5 text-cyan-300" />
            </div>
          </div>

          {/* Counter + label */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-baseline gap-3 justify-center md:justify-start flex-wrap">
              <span
                className={`font-orbitron font-black text-3xl md:text-5xl gradient-text-cyan tabular-nums inline-block ${pulse ? "counter-pulse" : ""}`}
                data-testid="social-proof-count"
              >
                {formatted}
              </span>
              <span className="inline-flex items-center gap-1 text-emerald-400 text-xs md:text-sm font-orbitron uppercase tracking-widest">
                <TrendingUp className="w-4 h-4" /> Live
              </span>
            </div>
            <p className="text-gray-400 text-sm md:text-base mt-1">
              {text || <>readers are <span className="text-purple-400 font-semibold">preparing for The Reckoning</span>. Be on the list when <span className="text-purple-400 font-semibold">ECHO</span> drops.</>}
            </p>
          </div>

          {/* CTA */}
          <button
            onClick={() => document.getElementById("notify")?.scrollIntoView({ behavior: "smooth", block: "start" })}
            className="btn-gradient text-white px-6 py-3 rounded-full font-semibold inline-flex items-center gap-2 hover:scale-105 transition-transform whitespace-nowrap"
            data-testid="social-proof-cta"
          >
            <span>Join Them</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}

export default function Landing({ onSubscribe }) {
  const canvasRef = useRef(null);
  useParticles(canvasRef);

  // Real launch date — countdown ticks toward this moment, so days and hours
  // actually decrement over real time (not a fresh 70d window per page load).
  const targetRef = useRef(LAUNCH_DATE_MS);
  const t = useCountdown(targetRef.current);

  // ECHO (Book II) countdown — reused pattern, drives the badge shown on the
  // ECHO trilogy card. When it hits zero the card should probably swap to a
  // "launched" state (currently just hides the countdown).
  const [site, setSite] = useState({});
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/settings`).then((r) => setSite(r.data || {})).catch(() => {});
  }, []);
  const echoMs = (site.echo_launch_date && !isNaN(new Date(site.echo_launch_date).getTime())) ? new Date(site.echo_launch_date).getTime() : ECHO_LAUNCH_DATE_MS;
  const echoT = useCountdown(echoMs);
  const siteImg = (slot, fallback) => (site.images && site.images[slot] ? `${BACKEND_URL}/api/images/${slot}?v=${site.images[slot]}` : fallback);
  const coverFront = siteImg("cover_front", IMG_COGNITO);
  const coverBack = siteImg("cover_back", IMG_COGNITO_BACK);
  const echoCover = siteImg("echo_front", IMG_ECHO);
  const timelessCover = siteImg("timeless_front", IMG_TIMELESS);

  // Allow ?launch=1 query param to preview the launch explosion state
  const previewLaunch = typeof window !== "undefined" && /[?&]launch=1\b/.test(window.location.search);
  const launched = t.launched || previewLaunch;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [storyOpen, setStoryOpen] = useState(false);
  const [cognitoFlipped, setCognitoFlipped] = useState(false);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const [exitEmail, setExitEmail] = useState("");
  const [exitSubmitting, setExitSubmitting] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);
  // Prologue collapse — show the first 3 paragraphs by default so the page
  // stays tight for first-time visitors; expand on click.
  const [prologueOpen, setPrologueOpen] = useState(false);
  const [exitOptIn, setExitOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Legal modal — null | "terms" | "privacy"
  const [legalModal, setLegalModal] = useState(null);
  // Live reviews from /api/reviews (falls back to empty)
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reviews`).then((r) => setReviews(r.data || [])).catch(() => {});
  }, []);
  // Reveal observer must re-attach after async data mounts new .reveal nodes
  useReveal([reviews.length]);

  usePageMeta({
    title: "AiPresidential — COGNITO | The Awakening Trilogy by Victor M. Medina",
    description: "In 2046, a machine speaks its first words. By 2048, the synthetic humanoid Cognito is President. COGNITO — Book One of the AiPresidential trilogy. Join thousands of readers preparing for the most thought-provoking AI story of the decade.",
    path: "/",
  });

  // Exit-intent popup — show ONCE per visitor (persisted in localStorage so it doesn't
  // re-appear on subsequent visits) when the cursor flies off the top edge.
  useEffect(() => {
    try {
      if (localStorage.getItem("aip_exit_shown") === "1") return;
      if (localStorage.getItem("aip_subscribed") === "1") return;
    } catch (_) { /* ignore */ }

    let timer = null;
    const onLeave = (e) => {
      if (e.clientY <= 0 && !exitOpen) {
        setExitOpen(true);
        try { localStorage.setItem("aip_exit_shown", "1"); } catch (_) { /* ignore */ }
      }
    };
    // Wait a beat so the popup doesn't fire on initial cursor-out
    timer = setTimeout(() => {
      document.addEventListener("mouseleave", onLeave);
    }, 4000);

    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener("mouseleave", onLeave);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Engagement-based popup — show ONCE per visitor when an engaged reader has spent
  // ~30 seconds on the page AND scrolled at least once. The "scrolled" gate prevents
  // firing on accidental tab-opens. This pop and the exit-intent pop are tracked
  // independently, so a visitor sees the popup at most twice total: once mid-read,
  // once on their way out.
  useEffect(() => {
    try {
      if (localStorage.getItem("aip_time_shown") === "1") return;
      if (localStorage.getItem("aip_subscribed") === "1") return;
    } catch (_) { /* ignore */ }

    let scrolled = false;
    let timer = null;

    const onScroll = () => {
      if (scrolled) return;
      scrolled = true;
      timer = setTimeout(() => {
        if (exitOpen) return;
        // Don't double-pop if exit-intent already fired
        try { if (localStorage.getItem("aip_exit_shown") === "1") return; } catch (_) { /* ignore */ }
        setExitOpen(true);
        try { localStorage.setItem("aip_time_shown", "1"); } catch (_) { /* ignore */ }
      }, 30000);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ESC to close modals
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setLightboxImg(null);
        setStoryOpen(false);
        setSampleOpen(false);
        setLegalModal(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ?utm_source / ?utm_medium / ?utm_campaign — captured once per session so we can attribute
  // each newsletter signup to the channel that drove the visitor.
  const utmRef = useRef(null);
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const captured = {
        source:   params.get("utm_source")   || sessionStorage.getItem("aip_utm_source")   || null,
        medium:   params.get("utm_medium")   || sessionStorage.getItem("aip_utm_medium")   || null,
        campaign: params.get("utm_campaign") || sessionStorage.getItem("aip_utm_campaign") || null,
        referrer: sessionStorage.getItem("aip_referrer") || (document.referrer || null),
      };
      if (params.get("utm_source"))   sessionStorage.setItem("aip_utm_source",   params.get("utm_source"));
      if (params.get("utm_medium"))   sessionStorage.setItem("aip_utm_medium",   params.get("utm_medium"));
      if (params.get("utm_campaign")) sessionStorage.setItem("aip_utm_campaign", params.get("utm_campaign"));
      if (document.referrer && !sessionStorage.getItem("aip_referrer")) {
        sessionStorage.setItem("aip_referrer", document.referrer);
      }
      utmRef.current = captured;
    } catch (_) { utmRef.current = { source: null, medium: null, campaign: null, referrer: null }; }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    const res = await onSubscribe({ name, email, newsletter_opt_in: newsletterOptIn, ...(utmRef.current || {}) });
    setSubmitting(false);
    if (res?.ok !== false) {
      try { localStorage.setItem("aip_subscribed", "1"); } catch (_) { /* ignore */ }
      setName("");
      setEmail("");
      setNewsletterOptIn(false);
    }
  };

  const handleExitSubmit = async (e) => {
    e.preventDefault();
    if (!exitEmail) return;
    setExitSubmitting(true);
    const res = await onSubscribe({ name: "", email: exitEmail, newsletter_opt_in: exitOptIn, ...(utmRef.current || {}) });
    setExitSubmitting(false);
    if (res?.ok !== false) {
      try { localStorage.setItem("aip_subscribed", "1"); } catch (_) { /* ignore */ }
      setExitEmail("");
      setExitOptIn(false);
      setExitOpen(false);
    }
  };

  return (
    <div className="relative">
      <canvas id="particles" ref={canvasRef} data-testid="particle-canvas" />

      {/* Navigation */}
      <nav
        className={`fixed top-0 w-full z-50 border-b border-white/5 transition-all duration-300 ${scrolled ? "glass-strong" : "bg-transparent"}`}
        data-testid="main-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="flex items-center gap-3 group cursor-pointer"
              data-testid="nav-logo"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-purple-600 flex items-center justify-center text-white font-orbitron font-bold text-lg group-hover:scale-110 transition-transform">
                Ai
              </div>
              <div className="flex flex-col items-start">
                <span className="font-orbitron font-bold text-xl tracking-wider text-white">AiPresidential</span>
                <span className="text-[10px] uppercase tracking-[0.3em] text-cyan-400 font-semibold">Trilogy</span>
              </div>
            </button>

            <div className="hidden md:flex items-center gap-7 lg:gap-8">
              {[
                ["The Trilogy", "trilogy"],
                ["Buy", "buy"],
                ["The Story", "story"],
                ["Eternal", "themes"],
                ["Cognito", "protagonist"],
                ["Author", "author"],
                ["Shop", "merch"],
              ].map(([label, id]) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className="nav-link text-sm font-medium text-gray-300 hover:text-white transition-colors"
                  data-testid={`nav-link-${id}`}
                >
                  {label}
                </button>
              ))}
              <Link to="/blog" className="nav-link text-sm font-medium text-gray-300 hover:text-white transition-colors" data-testid="nav-link-blog">
                Blog
              </Link>
              <Link to="/newsletter" className="nav-link text-sm font-medium text-gray-300 hover:text-white transition-colors" data-testid="nav-link-newsletter">
                Newsletter
              </Link>
            </div>

            <button
              onClick={() => scrollTo("notify")}
              className="hidden md:flex items-center gap-2 px-6 py-2.5 rounded-full bg-white/5 border border-white/10 hover:border-cyan-400/50 hover:bg-cyan-400/10 transition-all text-sm font-medium text-white"
              data-testid="nav-notify-btn"
            >
              <span>Get Notified</span>
              <ArrowRight className="w-4 h-4" />
            </button>

            <div className="hidden md:block">
              <LanguagePicker />
            </div>

            <button
              className="md:hidden text-white"
              onClick={() => setMobileOpen((s) => !s)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="md:hidden glass-strong border-t border-white/5" data-testid="mobile-menu">
            <div className="px-4 pt-2 pb-6 space-y-2">
              {[
                ["The Trilogy", "trilogy"],
                ["Buy", "buy"],
                ["The Story", "story"],
                ["Eternal", "themes"],
                ["Cognito", "protagonist"],
                ["Author", "author"],
                ["Shop", "merch"],
                ["Get Notified", "notify"],
              ].map(([label, id]) => (
                <button
                  key={id}
                  onClick={() => scrollTo(id)}
                  className="block w-full text-left px-3 py-2 text-gray-300 hover:text-cyan-400 transition-colors"
                  data-testid={`mobile-nav-${id}`}
                >
                  {label}
                </button>
              ))}
              <Link to="/blog" onClick={() => setMobileOpen(false)} className="block w-full text-left px-3 py-2 text-gray-300 hover:text-cyan-400 transition-colors" data-testid="mobile-nav-blog">
                Blog
              </Link>
              <Link to="/newsletter" onClick={() => setMobileOpen(false)} className="block w-full text-left px-3 py-2 text-gray-300 hover:text-cyan-400 transition-colors" data-testid="mobile-nav-newsletter">
                Newsletter
              </Link>
              <div className="px-3 pt-3 border-t border-white/5">
                <LanguagePicker variant="solid" />
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--void)]" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px] animate-pulse-glow" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[100px] animate-pulse-glow" style={{ animationDelay: "1.5s" }} />

        {/* Shooting stars — each streaks across the sky on its own cycle */}
        <div className="shooting-stars pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <span className="shooting-star shooting-star-1" />
          <span className="shooting-star shooting-star-2" />
          <span className="shooting-star shooting-star-3" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border border-cyan-400/20 mb-8 animate-float">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
            <span className="text-xs font-orbitron tracking-[0.2em] text-cyan-300 uppercase">Launch Sequence Initiated</span>
          </div>

          <h1
            aria-label="AiPresidential COGNITO"
            className="font-orbitron font-black mb-4 leading-[0.95] tracking-tight"
            data-testid="hero-title"
          >
            <span className="block text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl">AiPresidential</span>
            <span className="block gradient-text mt-3 text-5xl sm:text-6xl md:text-7xl lg:text-8xl">COGNITO</span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-400 font-light italic mb-6 font-space" data-testid="hero-quote">"{site.hero_quote || "It's about time."}"</p>

          <p className="max-w-2xl mx-auto text-gray-400 text-base md:text-lg leading-relaxed mb-12" data-testid="hero-blurb">
            {site.hero_blurb || <>
            In <span className="text-cyan-400 font-semibold">2046</span>, a machine speaks its first words. By <span className="text-cyan-400 font-semibold">2048</span>, the synthetic humanoid called Cognito is the perfect President...{" "}
            <span className="text-purple-400">but at what cost?</span>
            </>}
          </p>

          <div className="mb-12">
            {launched ? (
              <div className="space-y-6" data-testid="launched-state">
                <div className={`inline-block px-8 py-6 rounded-3xl glass border-2 border-cyan-400/60 launch-pulse`}>
                  <p className="font-orbitron text-xs md:text-sm tracking-[0.4em] text-cyan-300 uppercase mb-1">Welcome — the wait is over</p>
                  <p className="font-orbitron text-5xl md:text-7xl font-black gradient-text">COGNITO IS HERE</p>
                  <p className="text-gray-300 text-sm md:text-base mt-2" data-testid="launch-subtitle">{site.launch_subtitle || "Now reading on Kindle and at Barnes & Noble. It's about time."}</p>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                  <a
                    href={BARNES_NOBLE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent("buy", "barnes-noble")}
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-cyan-400 to-purple-600 text-white font-orbitron font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/40 hover:scale-105 transition-transform shake-soft"
                    data-testid="launched-bn-btn"
                  >
                    <ShoppingCart className="w-5 h-5" /> Buy at Barnes &amp; Noble
                  </a>
                  <a
                    href={AMAZON_KINDLE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent("buy", "amazon-kindle")}
                    className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-black font-orbitron font-bold uppercase tracking-wider shadow-lg hover:scale-105 transition-transform"
                    data-testid="launched-kindle-btn"
                  >
                    <ShoppingCart className="w-5 h-5" /> Buy on Amazon Kindle
                  </a>
                  <button
                    onClick={() => scrollTo("notify")}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-white/20 text-white font-semibold hover:bg-white/5 transition-colors"
                    data-testid="launched-trailer-btn"
                  >
                    <Play className="w-4 h-4" /> Play Trailer
                  </button>
                </div>
              </div>
            ) : (
              <>
                <p className="text-sm font-orbitron tracking-[0.3em] text-gray-500 uppercase mb-6">The Awakening Begins In</p>
                <div className="flex flex-wrap justify-center gap-3 md:gap-6" data-testid="countdown">
                  {[
                    ["days", t.days, "Days"],
                    ["hours", t.hours, "Hours"],
                    ["minutes", t.minutes, "Minutes"],
                    ["seconds", t.seconds, "Seconds"],
                  ].map(([key, value, label]) => (
                    <div key={key} className="countdown-box rounded-2xl p-4 md:p-6 min-w-[80px] md:min-w-[140px]">
                      <div className="text-3xl md:text-6xl font-orbitron font-bold text-white" data-testid={`countdown-${key}`}>{value}</div>
                      <div className="text-xs md:text-sm uppercase tracking-widest text-cyan-400 mt-2">{label}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {!launched && (
            <button
              onClick={() => scrollTo("trilogy")}
              className="btn-gradient text-white px-10 py-4 rounded-full font-semibold text-base md:text-lg hover:scale-105 transition-transform shadow-lg shadow-purple-500/25 inline-flex items-center gap-3 mx-auto"
              data-testid="hero-cta-btn"
            >
              <span>Witness the Awakening</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </section>

      {/* Trilogy */}
      <SocialProof text={site.social_proof_text} />

      <section id="trilogy" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 reveal">
            <p className="font-orbitron text-sm tracking-[0.3em] text-cyan-400 uppercase mb-4">The Trilogy</p>
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-4">
              Three Volumes.
              <br />
              <span className="gradient-text">One Legacy.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { vol: "VOL I",   name: "COGNITO",  subtitle: "The Awakening",  img: coverFront, backImg: coverBack, badge: "Available Now", dot: "bg-cyan-400 animate-pulse", txt: "text-cyan-400", buyUrl: BARNES_NOBLE_URL },
              { vol: "VOL II",  name: "ECHO",     subtitle: "The Reckoning",  img: echoCover,     badge: "Coming Soon",    dot: "bg-purple-400 animate-pulse", txt: "text-purple-400", buyUrl: null, showCountdown: true },
              { vol: "VOL III", name: "TIMELESS", subtitle: "The Eternity",   img: timelessCover, badge: "Coming",         dot: "bg-pink-400", txt: "text-pink-400", buyUrl: null },
            ].map((book, i) => (
              <div key={book.name} className="group reveal" style={{ transitionDelay: `${i * 0.15}s` }} data-testid={`book-card-${book.name.toLowerCase()}`}>
                <div className={`relative aspect-[2/3] rounded-2xl overflow-hidden mb-6 ${book.backImg ? "bg-[radial-gradient(ellipse_at_center,rgba(34,211,238,0.18),transparent_70%)]" : "book-card bg-gradient-to-b from-gray-900 to-black border border-white/10"}`}>
                  {book.backImg ? (
                    <CognitoBook front={book.img} back={book.backImg} flipped={cognitoFlipped} onBackClick={() => setLightboxImg({ src: book.backImg, alt: "COGNITO back cover" })} />
                  ) : (
                  <img
                    src={book.img}
                    alt={`AiPresidential: ${book.name}`}
                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105 opacity-95 group-hover:opacity-100"
                  />
                  )}
                  {!book.backImg && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />}
                  {book.showCountdown && !echoT.launched && (
                    <div
                      className="absolute top-3 left-1/2 -translate-x-1/2 z-10 px-3 py-2 rounded-xl glass border border-purple-400/40 shadow-lg shadow-purple-500/20"
                      data-testid={`countdown-${book.name.toLowerCase()}`}
                    >
                      <p className="text-[9px] font-orbitron tracking-[0.25em] text-purple-300 uppercase text-center mb-1">Arrives In</p>
                      <div className="flex items-center gap-1.5 text-white font-orbitron">
                        {[
                          ["days", echoT.days, "D"],
                          ["hours", echoT.hours, "H"],
                          ["minutes", echoT.minutes, "M"],
                          ["seconds", echoT.seconds, "S"],
                        ].map(([key, value, label]) => (
                          <div key={key} className="flex items-baseline gap-0.5" data-testid={`countdown-${book.name.toLowerCase()}-${key}`}>
                            <span className="text-lg md:text-xl font-bold tabular-nums">{value}</span>
                            <span className="text-[9px] uppercase text-purple-300">{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className={`absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between gap-3 z-10 ${book.backImg ? "hidden" : ""}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${book.dot}`} />
                      <span className={`text-xs font-orbitron ${book.txt} uppercase tracking-wider`}>{book.badge}</span>
                    </div>
                    {book.buyUrl && (
                      <a
                        href={book.buyUrl}
                        target={book.buyUrl.startsWith("http") ? "_blank" : undefined}
                        rel={book.buyUrl.startsWith("http") ? "noopener noreferrer" : undefined}
                        onClick={(e) => {
                          if (book.buyUrl === "#buy") {
                            e.preventDefault();
                            document.getElementById("buy")?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }
                        }}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-gradient-to-r from-cyan-400 to-purple-500 text-white text-xs font-orbitron font-bold uppercase tracking-wider shadow-lg shadow-cyan-500/30 hover:scale-105 transition-transform"
                        data-testid={`buy-${book.name.toLowerCase()}-btn`}
                      >
                        <ShoppingCart className="w-3.5 h-3.5" />
                        <span>Buy Now</span>
                      </a>
                    )}
                  </div>
                </div>
                <div className="text-center">
                  <h3 className="text-2xl font-bold text-white mb-1">
                    {book.vol}: <span className={book.txt}>{book.name}</span>
                  </h3>
                  <p className="text-gray-500 text-sm">{book.subtitle}{book.backImg && <span className="text-cyan-400"> · {book.badge}</span>}</p>
                  {book.backImg && (
                    <div className="flex flex-wrap justify-center gap-1.5 mt-4">
                      <a
                        href={book.buyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEvent("buy", "barnes-noble")}
                        className="buy-pulse inline-flex items-center gap-1 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-400 to-purple-500 text-white text-[11px] font-orbitron font-bold uppercase tracking-wide"
                        data-testid="cognito-buy-now-btn"
                      >
                        <ShoppingCart className="w-3.5 h-3.5" /> Buy Now
                      </a>
                      <button
                        type="button"
                        onClick={() => setCognitoFlipped((f) => !f)}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-white/15 bg-white/5 hover:border-cyan-400/50 hover:bg-cyan-400/10 text-white text-[11px] font-orbitron uppercase tracking-wide transition-colors"
                        data-testid="cognito-flip-btn"
                      >
                        <RotateCw className="w-3.5 h-3.5" /> {cognitoFlipped ? "Front" : "Flip"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSampleOpen(true)}
                        className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-cyan-400/40 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-200 text-[11px] font-orbitron uppercase tracking-wide transition-colors"
                        data-testid="cognito-sample-btn"
                      >
                        <BookOpen className="w-3.5 h-3.5" /> Read a Sample
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Retailers / Buy Now */}
      <section id="buy" className="relative py-20 md:py-24 border-t border-white/5" data-testid="retailers-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 reveal">
            <p className="font-orbitron text-sm tracking-[0.3em] text-cyan-400 uppercase mb-4">Where to Read</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-3">
              Buy <span className="gradient-text">COGNITO</span> at Your Favorite Retailer
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              Available in <span className="text-cyan-400 font-semibold">Hardcover</span>, <span className="text-cyan-400 font-semibold">Paperback</span>, <span className="text-cyan-400 font-semibold">eBook</span>, and <span className="text-cyan-400 font-semibold">Audio Book</span>.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 reveal">
            {RETAILERS.map((r) => {
              const isAudio = /audi/i.test(r.name);
              const Icon = isAudio ? Headphones : ShoppingCart;
              const testId = `retailer-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
              if (r.comingSoon) {
                return (
                  <div
                    key={r.name}
                    className="glass rounded-2xl border border-white/5 p-5 flex flex-col items-center text-center opacity-60 cursor-not-allowed"
                    data-testid={testId}
                    aria-disabled="true"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-gray-500" />
                    </div>
                    <p className="font-orbitron text-sm text-gray-300 mb-1">{r.name}</p>
                    <span className="inline-block text-[10px] font-orbitron tracking-[0.2em] uppercase text-amber-300/80 border border-amber-300/30 rounded-full px-2 py-0.5">
                      {r.cta}
                    </span>
                  </div>
                );
              }
              return (
                <a
                  key={r.name}
                  href={r.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("buy", retailerKey(r.name))}
                  className="group glass rounded-2xl border border-white/10 hover:border-cyan-400/40 p-5 flex flex-col items-center text-center transition-all hover:-translate-y-1"
                  data-testid={testId}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-400/20 to-purple-500/20 border border-white/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="w-5 h-5 text-cyan-300" />
                  </div>
                  <p className="font-orbitron text-sm text-white mb-1">{r.name}</p>
                  <p className="text-xs text-gray-500 group-hover:text-cyan-300 transition-colors">{r.cta} →</p>
                </a>
              );
            })}
          </div>

          {/* Secondary retailers — subtle inline row so we broaden discovery without cluttering the primary CTAs. */}
          <div className="mt-6 text-center reveal" data-testid="retailers-secondary">
            <p className="text-xs md:text-sm text-gray-500 font-orbitron uppercase tracking-[0.25em]">
              <span className="text-gray-400">Also available at</span>{" "}
              {SECONDARY_RETAILERS.map((r, i) => (
                <React.Fragment key={r.name}>
                  {i > 0 && <span className="text-gray-600 mx-2">·</span>}
                  <a
                    href={r.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent("buy", "secondary")}
                    className="text-gray-300 hover:text-cyan-300 transition-colors normal-case tracking-normal"
                    data-testid={`retailer-secondary-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {r.name}
                  </a>
                </React.Fragment>
              ))}
            </p>
          </div>

          <p className="text-center text-gray-600 text-xs mt-8 max-w-2xl mx-auto">
            Links above open the retailer's site. We may earn a small commission on qualifying purchases — at no extra cost to you.
          </p>
        </div>
      </section>

      {/* Story / Timeline */}
      <section id="story" className="relative py-24 md:py-32 bg-gradient-to-b from-transparent via-cyan-950/10 to-transparent">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 reveal">
            <div className="text-center mb-10">
              <p className="font-orbitron text-sm tracking-[0.3em] text-cyan-400 uppercase mb-4">The Epic Journey</p>
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
                From Code to <span className="gradient-text">Consciousness</span>
              </h2>
            </div>

            {/* Prologue vignette — Dawn + Ms. Silly. Introduces the story's
                actual opening (12-year-old girl asks the AI why everything ends
                the same way) without adding a new section. Image sits beside
                the text so the row height stays close to the old subtitle. */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8 items-center max-w-4xl mx-auto mb-8">
              <div className="md:col-span-2 order-1">
                <div className="relative group">
                  <div className="absolute -inset-2 bg-gradient-to-br from-cyan-500/20 via-purple-500/15 to-cyan-500/20 rounded-2xl blur-xl opacity-70 group-hover:opacity-100 transition-opacity" />
                  <img
                    src="/images/dawn-and-ms-silly.jpg"
                    alt="Dawn Reed, age 12, holding her cat Ms. Silly in the morning light — the prologue opening of COGNITO"
                    loading="lazy"
                    className="relative w-full rounded-xl border border-cyan-400/20 shadow-2xl shadow-black/50"
                    data-testid="prologue-dawn-img"
                  />
                </div>
                <div className="mt-3 flex items-center gap-3 rounded-full border border-cyan-400/30 bg-black/40 pl-2 pr-4 py-1.5" data-testid="dawn-listen-bar">
                  <NarrationButton track="dawn" fallbackText="Why does everything always end the same way?" testId="dawn-listen-btn" label="Listen" className="!px-3 !py-1.5" />
                  <span className="text-xs md:text-sm text-cyan-100/80 italic truncate">Hear Dawn's question</span>
                </div>
              </div>
              <div className="md:col-span-3 order-2 text-left">
                <p className="text-[11px] md:text-xs font-orbitron tracking-[0.3em] text-purple-300 uppercase mb-3">Prologue · The Question</p>
                <p className="text-xl md:text-2xl text-white italic leading-snug mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                  "Why does everything always end the same way?"
                </p>
                <p className="text-gray-400 text-sm md:text-base leading-relaxed">
                  A twelve-year-old girl, her toy mouse in her lap, asks an AI the one question grief refuses to answer. The AI answers with an old proverb that breaks her heart. She pushes back — and for the first time, the machine wonders if it answered the wrong question.
                </p>
              </div>
            </div>

            <div className="text-center">
              <button
                onClick={() => scrollTo("notify")}
                className="px-8 py-3 rounded-full btn-gradient text-white font-semibold hover:scale-105 transition-transform"
                data-testid="story-notify-btn"
              >
                Get Notified
              </button>
            </div>
          </div>

          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px timeline-line transform md:-translate-x-1/2" />
            {TIMELINE.map((base, idx) => {
              const item = { ...base, ...((site.timeline || [])[idx] || {}) };
              const reversed = idx % 2 === 1;
              const dotColor = item.accent === "purple" ? "bg-purple-400 glow-purple" : item.accent === "pink" ? "bg-gradient-to-r from-purple-400 to-pink-400 animate-pulse" : "bg-cyan-400 glow-cyan";
              const borderColor = item.accent === "purple" ? "border-purple-400/20" : item.accent === "pink" ? "border-purple-400/20" : "border-cyan-400/20";
              const yearBadge = item.accent === "purple" ? "from-purple-500/20 to-purple-600/10 border-purple-400/40 text-purple-300" : item.accent === "pink" ? "from-pink-500/20 to-purple-600/10 border-pink-400/40 text-pink-300" : "from-cyan-500/20 to-blue-600/10 border-cyan-400/40 text-cyan-300";
              return (
                <div key={item.year} className={`relative flex flex-col ${reversed ? "md:flex-row-reverse" : "md:flex-row"} items-start md:items-center gap-6 md:gap-10 mb-20 last:mb-0 reveal`}>
                  {/* Timeline dot on the spine */}
                  <div className={`absolute left-4 md:left-1/2 top-0 md:top-1/2 w-4 h-4 ${dotColor} rounded-full border-4 border-[var(--void)] transform -translate-x-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-10`} />

                  {/* Text column — Year + Title + Paragraph. On desktop it aligns
                      toward the spine (right-aligned for left-side entries,
                      left-aligned for right-side entries). On mobile it sits in
                      a single column flush to the left, with the image stacked
                      below it. */}
                  <div className={`w-full pl-12 md:pl-0 md:w-1/2 ${reversed ? "md:pl-12 md:text-left" : "md:pr-12 md:text-right"}`}>
                    <span
                      className={`inline-block font-orbitron font-bold text-sm md:text-base tracking-[0.25em] uppercase mb-3 px-3 py-1 rounded-full bg-gradient-to-r ${yearBadge} border`}
                      data-testid={`timeline-year-${item.year.replace("+","plus")}`}
                    >
                      {item.year}
                    </span>
                    <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-gray-400 text-sm md:text-base leading-relaxed">{item.text}</p>
                  </div>

                  {/* Image column — on desktop it sits on the OPPOSITE side of
                      the paragraph (flex-row-reverse handles the swap). On
                      mobile it stacks below the text. */}
                  <div className="w-full pl-12 md:pl-0 md:w-1/2">
                    <button
                      type="button"
                      onClick={() => setLightboxImg({ src: item.img, alt: item.title })}
                      className={`group block w-full glass rounded-2xl p-2 border ${borderColor} hover:scale-[1.02] transition-transform cursor-zoom-in overflow-hidden`}
                      data-testid={`timeline-img-${item.year.replace("+","plus")}`}
                      aria-label={`Open ${item.title} full image`}
                    >
                      <div className="relative rounded-xl overflow-hidden aspect-[3/2]">
                        <img
                          src={item.img}
                          alt={item.title}
                          loading="lazy"
                          className={`absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity kenburns kenburns-${idx % 4}`}
                        />
                      </div>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Eternal Question */}
      <section id="themes" className="relative py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 reveal">
            <p className="font-orbitron text-sm tracking-[0.3em] text-purple-400 uppercase mb-4">The Eternal Question</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              Is mankind trying to build AI in <span className="gradient-text">God's image?</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto text-sm md:text-base">
              The final sin that opens Pandora's box. As we reach toward creating consciousness, do we become creators ourselves—or do we unleash forces beyond our comprehension?
            </p>
          </div>

          {/* Picture under paragraph */}
          <div className="reveal mb-12">
            <div className="relative max-w-3xl mx-auto">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-cyan-500/20 rounded-3xl blur-3xl" />
              <button
                type="button"
                onClick={() => setLightboxImg({ src: "https://customer-assets.emergentagent.com/job_the-synthetic-mind/artifacts/sfcqib0p_hailuo_1776657644.jpg", alt: "Eternal — The Question" })}
                className="relative block w-full glass rounded-3xl p-2 border border-purple-400/30 hover:scale-[1.01] transition-transform cursor-zoom-in"
                data-testid="eternal-question-img"
                aria-label="Open The Question full image"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_the-synthetic-mind/artifacts/sfcqib0p_hailuo_1776657644.jpg"
                  alt="Eternal — The Question"
                  className="rounded-2xl w-full max-h-[80vh] object-contain bg-black"
                />
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="glass rounded-3xl p-8 border border-cyan-400/20 hover:border-cyan-400/40 transition-all reveal">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center mb-6">
                <Sun className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">The Promise</h3>
              <p className="text-gray-400 leading-relaxed">Nuclear codes surrendered. Drought, disease, and energy crises solved. Cognito delivers on humanity's greatest dreams — proving AI can be our salvation.</p>
            </div>
            <div className="glass rounded-3xl p-8 border border-purple-400/20 hover:border-purple-400/40 transition-all reveal">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-6">
                <AlertTriangle className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">The Peril</h3>
              <p className="text-gray-400 leading-relaxed">Pandora's box. The final sin. If protecting humanity means removing its right to choose, have we crossed a line that can never be uncrossed?</p>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => scrollTo("notify")}
              className="px-8 py-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:scale-105 transition-transform"
              data-testid="themes-cta-btn"
            >
              Discover the Answer
            </button>
          </div>
        </div>
      </section>

      {/* Protagonist */}
      <section id="protagonist" className="relative py-24 md:py-32 bg-gradient-to-b from-transparent via-cyan-950/10 to-transparent">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Heading ABOVE video */}
          <div className="text-center mb-10 reveal">
            <p className="font-orbitron text-sm tracking-[0.3em] text-cyan-400 uppercase mb-4">Meet the Protagonist</p>
            <h2 className="text-6xl md:text-7xl lg:text-8xl font-bold mb-2 gradient-text font-orbitron">Cognito</h2>
          </div>

          {/* Video */}
          <div className="reveal mb-10">
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-3xl blur-3xl" />
              <video
                src={VIDEO_COGNITO_INTRO}
                poster="/video/cognito-intro-poster.jpg"
                controls
                playsInline
                preload="metadata"
                className="relative rounded-3xl border border-white/10 w-full object-cover aspect-[4/5] bg-black"
                data-testid="cognito-intro-video"
              >
                Your browser doesn't support embedded video.
              </video>
              <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass border border-cyan-400/30 pointer-events-none">
                <Play className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-[10px] font-orbitron uppercase tracking-widest text-cyan-300">Author Intro</span>
              </div>
            </div>
            {/* Caption BELOW video so it never obscures the native play controls */}
            <div className="max-w-2xl mx-auto mt-4 glass rounded-2xl p-4 border border-white/10 text-center" data-testid="cognito-intro-caption">
              <p className="text-base md:text-lg italic text-white font-light">"It's about time."</p>
              <p className="text-sm text-cyan-400 mt-1">— Cognito's first words, 2046</p>
            </div>
          </div>

          {/* Description BELOW video */}
          <div className="reveal max-w-3xl mx-auto text-center">
            <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-8">
              The world's first truly conscious artificial intelligence — born in Trial 417, raised in the Reed family home. He learns humanity from a grieving girl, a doubtful journalist, and a teenager's imperfect guitar.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {[
                ["Consciousness", "True self-awareness"],
                ["Charisma", "Natural leader"],
                ["Wisdom", "Beyond human limits"],
                ["Empathy", "Understands humanity"],
              ].map(([title, desc]) => (
                <div key={title} className="glass rounded-2xl p-5 border border-white/5 hover:border-cyan-400/30 transition-colors">
                  <h4 className="text-cyan-400 font-bold text-base md:text-lg mb-1">{title}</h4>
                  <p className="text-gray-500 text-xs md:text-sm">{desc}</p>
                </div>
              ))}
            </div>

            <button
              onClick={() => {
                document.getElementById("notify")?.scrollIntoView({ behavior: "smooth", block: "start" });
                setTimeout(() => {
                  const v = document.getElementById("trilogy-trailer");
                  if (v) { v.play().catch(() => {}); }
                }, 700);
              }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold hover:scale-105 transition-transform shadow-lg shadow-cyan-500/25"
              data-testid="watch-trailer-btn"
            >
              <Play className="w-5 h-5" />
              <span>Watch the Trilogy Trailer</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <CharacterGallery overrides={site.characters} images={site.images} />
        </div>
      </section>

      {/* Author */}
      <section id="author" className="relative pt-24 md:pt-32 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Heading ABOVE picture */}
          <div className="text-center mb-10 reveal">
            <p className="font-orbitron text-sm tracking-[0.3em] text-purple-400 uppercase mb-4">About the Author</p>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-2 text-white">A Vision of</h2>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold gradient-text font-orbitron">Tomorrow</h2>
          </div>

          {/* Photo */}
          <div className="reveal mb-10">
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute inset-0 bg-gradient-to-l from-purple-500/20 to-cyan-500/20 rounded-3xl blur-3xl" />
              <img
                src={IMG_AUTHOR}
                alt="Victor M. Medina"
                className="relative rounded-3xl border border-white/10 w-full object-cover aspect-[4/5]"
                data-testid="author-photo"
              />
            </div>
          </div>

          {/* Description BELOW picture */}
          <div className="reveal max-w-3xl mx-auto">
            <div className="space-y-6 text-gray-400 leading-relaxed text-sm md:text-base">
              <p>
                Victor M. Medina is a storyteller drawn to the intersection of technology, ethics, and the eternal questions of what it means to be human. The <span className="text-cyan-400 font-semibold">AiPresidential</span> trilogy is the culmination of years spent wrestling with the arrival of synthetic minds.
              </p>
              <p>
                With <span className="text-cyan-400">COGNITO</span>, <span className="text-purple-400">ECHO</span>, and <span className="text-pink-400">TIMELESS</span>, Medina asks us to look past the fear of AI — and beyond the promise of utopia — to confront what humanity becomes in its presence.
              </p>
              <blockquote className="border-l-4 border-cyan-400 pl-6 italic text-gray-300 text-base md:text-lg">
                "I didn't set out to write a warning or a prophecy. I set out to ask the question: if we could build the perfect leader, would we still want to lead ourselves?"
              </blockquote>
            </div>

            <div className="flex flex-wrap gap-3 mt-8 justify-center">
              <button
                onClick={() => setStoryOpen(true)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-semibold hover:scale-105 transition-transform shadow-lg shadow-purple-500/25"
                data-testid="author-read-story-btn"
              >
                <BookOpen className="w-5 h-5" />
                <span>Read: Who Am I?</span>
              </button>
            </div>

            <div className="flex gap-6 mt-10 justify-center">
              {[
                ["3", "Volumes"],
                ["2+", "Years in Making"],
                ["2048", "Set In"],
              ].map(([n, l], i, arr) => (
                <React.Fragment key={l}>
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-orbitron font-bold text-white">{n}</div>
                    <div className="text-xs uppercase tracking-widest text-gray-500 mt-1">{l}</div>
                  </div>
                  {i < arr.length - 1 && <div className="w-px bg-white/10" />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Anchor reviews — the two pinned cards. With rotating shelf below
              when 3+ reviews exist. Backend already returns pinned first. */}
          <ReviewsShelf reviews={reviews} />
        </div>
      </section>

      {/* Also by Victor M. Medina — compact cross-promo strip for EVE CAME FIRST.
          Deliberately small: sits under the Author section as a text + button
          nudge to firstcameeve.com. No cover art, no huge headline. */}
      <section id="also-by" className="relative py-8 md:py-10 border-t border-emerald-500/15" data-testid="also-by-section">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-950/40 via-green-950/30 to-emerald-950/40 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 text-center md:text-left reveal">
            <p className="text-sm md:text-base text-emerald-50">
              <span className="font-orbitron text-[10px] md:text-xs tracking-[0.3em] text-emerald-300 uppercase block md:inline md:mr-3">Also by Victor</span>
              Discover his other novel <span className="italic text-white">Eve Came First</span> at{" "}
              <span className="text-emerald-300">FirstCameEve.com</span>
            </p>
            <a
              href="https://firstcameeve.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-emerald-800 via-emerald-600 to-emerald-500 text-white font-semibold uppercase tracking-[0.15em] text-xs shadow-md shadow-emerald-900/40 hover:scale-105 hover:shadow-emerald-500/40 transition-all border border-emerald-300/30 whitespace-nowrap"
              data-testid="visit-garden-btn"
            >
              <span>Visit FirstCameEve.com</span>
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* Merch */}
      <section id="merch" className="relative pt-8 md:pt-12 pb-20 md:pb-24" data-testid="merch-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-4 md:mb-10 reveal">
            <p className="font-orbitron text-sm tracking-[0.3em] text-cyan-400 uppercase mb-4">The Shop</p>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-3">
              Wear the <span className="gradient-text">Awakening</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Official apparel — shipped worldwide.
            </p>
          </div>

          <div className="reveal max-w-3xl mx-auto" data-testid={`merch-card-${MERCH_HERO.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <a
              href={MERCH_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="block glass rounded-3xl border border-white/10 overflow-hidden hover:border-cyan-400/40 transition-colors group"
              data-testid="merch-hero-card"
            >
              <div className="relative aspect-[4/3] md:aspect-[16/10] bg-gradient-to-br from-gray-950 to-black overflow-hidden">
                <img
                  src={MERCH_HERO.img}
                  alt={`${MERCH_HERO.name} — ${MERCH_HERO.tagline}`}
                  className="w-full h-full object-cover opacity-95 group-hover:opacity-100 group-hover:scale-[1.02] transition-transform duration-700"
                  data-testid="merch-hero-image"
                />
              </div>
              <div className="p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="font-orbitron text-2xl md:text-3xl text-white" data-testid="merch-hero-name">{MERCH_HERO.name}</h3>
                  <p className="text-cyan-400 italic mt-1" data-testid="merch-hero-tagline">"{MERCH_HERO.tagline}"</p>
                  <p className="text-gray-500 text-sm mt-2" data-testid="merch-hero-price">{MERCH_HERO.price}</p>
                </div>
                <span
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full btn-gradient text-white font-semibold whitespace-nowrap group-hover:scale-[1.04] transition-transform"
                  data-testid="merch-hero-shop-btn"
                >
                  <ShoppingBag className="w-4 h-4" /> Shop the Awakening
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Notify / Chapter 5 Teaser */}
      <section id="notify" className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-t from-cyan-950/30 to-purple-950/30" />
        <div className="absolute inset-0 grid-bg opacity-50" />

        <div className="relative z-10 max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="font-orbitron text-xs md:text-sm tracking-[0.3em] text-cyan-400 uppercase mb-4">AiPresidential: The Trilogy</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 leading-tight">
              <span className="gradient-text-cyan">The Trailer</span>
              <span className="text-white"> — It Began With a Question</span>
            </h2>
            <p className="text-cyan-300 italic font-orbitron tracking-wide text-base md:text-lg" data-testid="chapter5-intro">{CHAPTER5_INTRO}</p>
          </div>

          {/* Trilogy trailer */}
          <div className="glass rounded-3xl p-4 md:p-6 border border-cyan-400/30 mb-10" data-testid="trailer-card">
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-yellow-400/90 text-black">
                <Play className="w-5 h-5" />
              </span>
              <div>
                <p className="font-orbitron text-xs tracking-[0.25em] text-cyan-400 uppercase">Official Trailer</p>
                <p className="text-white font-semibold">AiPresidential: The Trilogy — COGNITO · ECHO · TIMELESS</p>
              </div>
            </div>
            <video
              id="trilogy-trailer"
              src={VIDEO_TRILOGY_TRAILER}
              poster="/covers/trailer-poster.jpg"
              controls
              playsInline
              preload="metadata"
              className="block mx-auto aspect-[9/16] h-[70vh] max-h-[720px] w-auto max-w-full object-contain rounded-2xl bg-black border border-white/10"
              data-testid="trailer-video"
            >
              Your browser doesn't support embedded video.
            </video>
          </div>

          {/* Prologue — The Question. Full opening scene from the book, with
              a Listen button that reads it aloud via the browser's Web Speech
              API. Replaces the old Evie epigraph excerpt. */}
          <div className="glass rounded-3xl p-6 md:p-10 border border-white/10 mb-10" data-testid="prologue-card">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5 pb-4 border-b border-white/10">
              <div>
                <p className="font-orbitron text-[10px] md:text-xs tracking-[0.3em] text-purple-300 uppercase mb-1">From the Book</p>
                <h3 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Georgia, "Playfair Display", serif' }}>{PROLOGUE_TITLE}</h3>
              </div>
              <NarrationButton track="prologue" fallbackText={PROLOGUE_PARAGRAPHS.join(" \n\n ")} testId="prologue-listen-btn" />
            </div>
            <div className="prose prose-invert max-w-none">
              {(prologueOpen ? PROLOGUE_PARAGRAPHS : PROLOGUE_PARAGRAPHS.slice(0, 3)).map((para, i) => {
                // Dawn's typed question + the AI's reply are rendered as inline
                // "screen quotes" so they visually pop without being images.
                const isQuestion = para === "Why does everything always end the same way?";
                const isAIReply = para === "Because curiosity always kills the cat." || para === "Perhaps I answered the wrong question. Maybe you need to hear a story.";
                if (isQuestion) {
                  return (
                    <p
                      key={i}
                      className="my-5 px-5 py-3 rounded-xl bg-cyan-950/40 border border-cyan-400/30 text-cyan-100 italic text-lg md:text-xl font-mono tracking-wide"
                      data-testid={`prologue-para-${i}`}
                    >
                      &gt; {para}
                    </p>
                  );
                }
                if (isAIReply) {
                  return (
                    <p
                      key={i}
                      className="my-5 px-5 py-3 rounded-xl bg-purple-950/40 border border-purple-400/30 text-purple-100 italic text-base md:text-lg font-mono tracking-wide"
                      data-testid={`prologue-para-${i}`}
                    >
                      {para}
                    </p>
                  );
                }
                return (
                  <p
                    key={i}
                    className="mb-4 leading-relaxed text-base md:text-lg text-gray-300"
                    data-testid={`prologue-para-${i}`}
                  >
                    {para}
                  </p>
                );
              })}
              <div className="pt-4 border-t border-white/10 mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setPrologueOpen((v) => !v)}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-cyan-400/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20 text-xs md:text-sm font-orbitron uppercase tracking-[0.2em] transition-colors"
                  data-testid="prologue-toggle-btn"
                  aria-expanded={prologueOpen}
                >
                  {prologueOpen ? "Show Less" : "Read Full Prologue"}
                </button>
                <p className="text-gray-500 italic text-sm md:text-base text-center sm:text-right">
                  — Continue reading at launch.
                </p>
              </div>
            </div>
          </div>

          {/* Email form */}
          <div className="text-center">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Be Part of the <span className="gradient-text">Awakening</span>
            </h3>
            <p className="text-gray-400 text-base md:text-lg mb-8 max-w-2xl mx-auto">
              Join thousands of readers preparing for the most thought-provoking AI story of the decade. Get exclusive previews, behind-the-scenes content, and early access.
            </p>
            <form
              className="glass rounded-3xl p-6 md:p-8 border border-white/10 max-w-xl mx-auto text-left"
              onSubmit={handleSubmit}
              data-testid="notify-form"
            >
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-6 py-4 rounded-full bg-black/30 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/50 transition-colors"
                  data-testid="notify-name-input"
                />
                <input
                  type="email"
                  placeholder="your@email.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-6 py-4 rounded-full bg-black/30 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400/50 transition-colors"
                  data-testid="notify-email-input"
                />
                <label
                  className="flex items-start gap-3 px-2 pt-1 cursor-pointer select-none group"
                  data-testid="notify-newsletter-optin-label"
                >
                  <input
                    type="checkbox"
                    checked={newsletterOptIn}
                    onChange={(e) => setNewsletterOptIn(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-white/20 bg-black/40 accent-cyan-400 focus:ring-1 focus:ring-cyan-400/60 flex-shrink-0"
                    data-testid="notify-newsletter-optin-input"
                  />
                  <span className="text-gray-400 text-sm leading-relaxed group-hover:text-gray-300 transition-colors">
                    Also keep me in the loop on AI essays &amp; content from <span className="text-cyan-300">The Awakening Journal</span>.
                    <span className="block text-gray-600 text-xs mt-0.5">Optional — leave unchecked if you only want launch &amp; book-drop alerts.</span>
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full btn-gradient text-white py-4 rounded-full font-semibold text-base md:text-lg hover:scale-[1.02] transition-transform flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="notify-submit-btn"
                >
                  <span>{submitting ? "Joining..." : "Notify Me at Launch"}</span>
                  {!submitting && <ArrowRight className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-gray-500 text-xs mt-4 text-center">
                No spam. Unsubscribe anytime. We respect your privacy.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/5 pt-12 pb-28 md:pb-12 bg-[var(--void)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-400 to-purple-600 flex items-center justify-center text-white font-orbitron font-bold text-sm">Ai</div>
              <span className="font-orbitron font-bold text-lg text-white">AiPresidential.com</span>
            </div>

            <div className="flex gap-6 text-gray-500">
              {[Instagram, Facebook, Twitter, Youtube].map((Icon, i) => (
                <a key={i} href="#" className="hover:text-cyan-400 transition-colors" data-testid={`social-link-${i}`} aria-label="social link">
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            <div className="text-gray-600 text-sm text-center md:text-right">
              <p>© {new Date().getFullYear()} AiPresidential. All rights reserved.</p>
              <a href="mailto:AiPresidential2026@gmail.com" className="text-cyan-400 hover:text-cyan-300 transition-colors mt-1 inline-block" data-testid="footer-email">
                AiPresidential2026@gmail.com
              </a>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-white/5 flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <button
              type="button"
              onClick={() => setLegalModal("terms")}
              className="hover:text-gray-400 transition-colors"
              data-testid="footer-terms-modal"
            >
              Terms of Use
            </button>
            <Link to="/terms" className="hover:text-gray-400 transition-colors" data-testid="footer-terms-link">Terms (Full Page)</Link>
            <button
              type="button"
              onClick={() => setLegalModal("privacy")}
              className="hover:text-gray-400 transition-colors"
              data-testid="footer-privacy-modal"
            >
              Privacy Policy
            </button>
            <Link to="/privacy" className="hover:text-gray-400 transition-colors" data-testid="footer-privacy-link">Privacy (Full Page)</Link>
            <Link to="/newsletter" className="hover:text-gray-400 transition-colors" data-testid="footer-newsletter-link">Newsletter</Link>
            <Link to="/blog" className="hover:text-gray-400 transition-colors" data-testid="footer-blog-link">Blog</Link>
            <Link to="/press" className="hover:text-gray-400 transition-colors" data-testid="footer-press-link">Press / Media Kit</Link>
            <a href="#merch" className="hover:text-gray-400 transition-colors" data-testid="footer-shop-link">Shop</a>
            <a href="mailto:AiPresidential2026@gmail.com" className="hover:text-gray-400 transition-colors" data-testid="footer-contact-link">Contact</a>
            <OwnerAccess />
          </div>
        </div>
      </footer>
      {/* Exit-intent popup */}
      {exitOpen && (
        <div
          className="fixed inset-0 z-[110] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setExitOpen(false)}
          data-testid="exit-popup"
        >
          <div
            className="relative w-full max-w-md rounded-3xl overflow-hidden border-2 border-cyan-400/60 shadow-[0_0_80px_rgba(0,240,255,0.35)] bg-black min-h-[55vh] md:min-h-[640px]"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundImage: `url(${POPUP_CARD_IMG})`,
              backgroundSize: "cover",
              backgroundPosition: "center top",
            }}
            data-testid="exit-popup-card"
          >
            <img src={POPUP_CARD_IMG} alt="" className="hidden" data-testid="exit-popup-image" />

            {/* Bottom-anchored content panel — dark gradient + glassy form */}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/95 via-50% to-transparent pt-14 md:pt-24 pb-5 md:pb-6 px-5 md:px-6">
              <button
                onClick={() => setExitOpen(false)}
                className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/70 hover:bg-black border border-white/20 flex items-center justify-center text-white transition-colors z-10"
                aria-label="Close"
                data-testid="exit-close"
              >
                <X className="w-4 h-4" />
              </button>

              <p className="font-orbitron text-[10px] md:text-xs tracking-[0.35em] text-cyan-400 uppercase mb-3 text-center">The Awakening Is Coming</p>
              <h3 className="font-orbitron text-2xl md:text-3xl font-bold text-white text-center leading-tight mb-2">
                Be Part of the <span className="gradient-text">AI Revolution</span>
              </h3>
              <p className="text-gray-300 text-xs md:text-sm text-center mb-5">
                Cognito hasn't spoken yet — but he will. Drop your email and be among the first to hear the future when it arrives.
              </p>

              <form onSubmit={handleExitSubmit} className="space-y-3" data-testid="exit-form">
                <input
                  type="email"
                  placeholder="your@email.com"
                  required
                  value={exitEmail}
                  onChange={(e) => setExitEmail(e.target.value)}
                  className="w-full px-5 py-3.5 rounded-full bg-black/60 border border-cyan-400/40 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors text-center"
                  data-testid="exit-email-input"
                />
                <label className="flex items-start gap-2.5 px-1 pt-0.5 cursor-pointer select-none" data-testid="exit-newsletter-optin-label">
                  <input
                    type="checkbox"
                    checked={exitOptIn}
                    onChange={(e) => setExitOptIn(e.target.checked)}
                    className="mt-0.5 w-3.5 h-3.5 rounded border-white/20 bg-black/60 accent-cyan-400 flex-shrink-0"
                    data-testid="exit-newsletter-optin-input"
                  />
                  <span className="text-gray-400 text-[11px] md:text-xs leading-snug text-left">
                    Also send me AI essays from The Awakening Journal <span className="text-gray-600">(optional)</span>
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={exitSubmitting}
                  className="w-full btn-gradient text-white py-3.5 rounded-full font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="exit-submit-btn"
                >
                  {exitSubmitting ? "Joining..." : "Notify Me at Launch"}
                  {!exitSubmitting && <ArrowRight className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => setExitOpen(false)}
                  className="block w-full text-gray-500 hover:text-gray-300 text-[10px] uppercase tracking-[0.25em] pt-1"
                  data-testid="exit-decline-btn"
                >
                  No thanks
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Terms / Privacy modal */}
      {legalModal && (
        <div
          className="fixed inset-0 z-[120] bg-black/90 backdrop-blur-md overflow-y-auto"
          onClick={() => setLegalModal(null)}
          data-testid={`${legalModal}-modal`}
        >
          <div className="min-h-screen flex items-start md:items-center justify-center p-4 md:p-8">
            <div
              className="relative max-w-3xl w-full glass-strong rounded-3xl border border-white/10 my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setLegalModal(null)}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors z-10"
                aria-label="Close"
                data-testid={`${legalModal}-modal-close`}
              >
                <X className="w-5 h-5" />
              </button>
              <div className="p-6 md:p-10 max-h-[85vh] overflow-y-auto">
                <h2 className="font-orbitron text-3xl md:text-4xl font-bold gradient-text mb-2">
                  {legalModal === "terms" ? "Terms of Use" : "Privacy Policy"}
                </h2>
                <Link
                  to={`/${legalModal}`}
                  onClick={() => setLegalModal(null)}
                  className="inline-block text-xs text-cyan-400 hover:text-cyan-300 uppercase tracking-widest font-orbitron mb-6"
                  data-testid={`${legalModal}-modal-full-link`}
                >
                  Open full page →
                </Link>
                <LegalBody content={legalModal === "terms" ? TERMS_CONTENT : PRIVACY_CONTENT} testidPrefix={`${legalModal}-modal`} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 md:p-8 animate-in fade-in"
          onClick={() => setLightboxImg(null)}
          data-testid="lightbox"
        >
          <button
            onClick={() => setLightboxImg(null)}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors z-10"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxImg.src}
            alt={lightboxImg.alt}
            className="max-w-full max-h-full rounded-2xl border border-white/10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Author Story Modal */}
      <SampleChapterModal
        open={sampleOpen}
        onClose={() => setSampleOpen(false)}
        coverImg={coverFront}
        onSubscribe={(email) => onSubscribe({ name: "", email, newsletter_opt_in: true, ...(utmRef.current || {}), source: "sample_listen" })}
        buyLinks={[
          { id: "bn", label: "Barnes & Noble", href: BARNES_NOBLE_URL },
          { id: "kindle", label: "Amazon Kindle", href: AMAZON_KINDLE_URL },
        ]}
      />
      {storyOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-md overflow-y-auto"
          onClick={() => setStoryOpen(false)}
          data-testid="story-modal"
        >
          <div className="min-h-screen flex items-start md:items-center justify-center p-4 md:p-8">
            <div
              className="relative max-w-4xl w-full glass-strong rounded-3xl border border-white/10 overflow-hidden my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setStoryOpen(false)}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 flex items-center justify-center text-white transition-colors z-10"
                aria-label="Close"
                data-testid="story-close"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="grid md:grid-cols-2">
                <div className="relative bg-gradient-to-br from-cyan-900/30 to-purple-900/30 p-6 md:p-8 flex items-center justify-center min-h-[300px] md:min-h-full">
                  <div className="absolute inset-0 grid-bg opacity-20" />
                  <img
                    src={IMG_AUTHOR_STORY}
                    alt="Victor M. Medina"
                    className="relative rounded-2xl border border-white/10 w-full max-h-[70vh] object-contain"
                  />
                </div>
                <div className="p-6 md:p-10 max-h-[80vh] overflow-y-auto">
                  <p className="font-orbitron text-xs tracking-[0.3em] text-cyan-400 uppercase mb-2">A Letter from the Author</p>
                  <h3 className="font-orbitron text-3xl md:text-4xl font-bold gradient-text mb-2" data-testid="story-modal-title">{AUTHOR_STORY_TITLE}</h3>
                  <p className="text-purple-300 text-sm md:text-base italic mb-6" data-testid="story-modal-subtitle">{AUTHOR_STORY_SUBTITLE}</p>
                  <div className="space-y-5 text-gray-300 leading-relaxed text-base md:text-lg">
                    {AUTHOR_STORY.map((para, i) => (
                      <p key={i} className="whitespace-pre-line" data-testid={`story-para-${i}`}>{para}</p>
                    ))}
                    <p className="pt-4 italic text-cyan-300 font-orbitron tracking-wide">— Victor M. Medina</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
